import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-bottom-sheet';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { formatUsdCurrency } from '@treasury/utils';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import { ReportMetaDataVm } from '../data/report-meta-data.vm';
import { renderAccountTransactionColumns } from './transaction-report-columns';
import {
    DateRangeTransactionReportItem,
    TransactionReportItemVm,
} from './transaction-report-item.vm';

export const tagName = 'transactions-account-detail-sheet';
@customElement(tagName)
export class TransactionsAccountDetailSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property()
    public account!: TransactionReportItemVm;

    @property()
    public report!: ReportMetaDataVm;

    @state()
    private filteredAccountTransactions!: DateRangeTransactionReportItem[] | undefined;

    @state()
    private fieldsToFilter = ['date', 'accountType', 'description', 'debits', 'credits'];

    public render() {
        return html`<tm-bottom-sheet
            horizontal
            .open=${this.open}
            @close=${() => {
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center" class="flex flex-col items-center">
                <h2 class="text-lg font-bold">${this.account?.accountName}</h2>
                <span class="text-sm">${this.account?.accountNumber}</span>
            </div>
            <tm-section>
                <div id="transactions-summary" class="flex justify-around px-5 py-2 items-center">
                    <div class="flex flex-col text-center text-lg">
                        <span class="text-secondary pl-1 font-light uppercase text-xs"
                            >Debit Amount</span
                        >

                        <span class="text-lg">${this.totalDebitsFormatted}</span>
                        <span class="text-secondary pr-1 font-light text-xs"
                            >(${this.debitsTotalCount} debits)
                        </span>
                    </div>
                    <div class="flex flex-col text-center text-lg">
                        <span class="text-secondary pl-1 font-light uppercase text-xs"
                            >Credit Amount</span
                        >
                        <span class="text-lg">${this.totalCreditsFormatted}</span>
                        <span class="text-secondary pr-1 font-light text-xs"
                            >(${this.creditsTotalCount} credits)
                        </span>
                    </div>
                </div>
            </tm-section>
            <tm-section class="mt-3">
                <tm-list-header
                    .headerTitle=${'Transactions'}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                    ><span class="text-xs text-secondary"
                        >${this.report?.date}</span
                    ></tm-list-header
                >
                <div id="transactions-list">
                    ${this.account
                        ? html`<vaadin-grid
                              .items=${this.filteredAccountTransactions}
                              all-rows-visible
                          >
                              ${renderAccountTransactionColumns()}
                          </vaadin-grid>`
                        : nothing}
                </div>
            </tm-section>
        </tm-bottom-sheet>`;
    }

    private get totalDebitsFormatted() {
        const total = this.account?.data?.reduce(
            (acc, curr) => acc + parseFloat(curr.debits.replace(/[^0-9.-]/g, '')),
            0
        );
        return formatUsdCurrency(total ?? 0);
    }

    private get debitsTotalCount() {
        return this.account?.data?.filter(item => item.isDebit).length ?? 0;
    }

    private get totalCreditsFormatted() {
        const total = this.account?.data?.reduce(
            (acc, curr) => acc + parseFloat(curr.credits.replace(/[^0-9.-]/g, '')),
            0
        );
        return formatUsdCurrency(total ?? 0);
    }

    private get creditsTotalCount() {
        return this.account?.data?.filter(item => !item.isDebit).length ?? 0;
    }

    public updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('account') && this.account) {
            this.filteredAccountTransactions = this.account
                .data as unknown as DateRangeTransactionReportItem[];
        }
    }

    private filterResults(e: CustomEvent) {
        this.filteredAccountTransactions = filterTableResults(
            e,
            this.account.data ?? [],
            this.fieldsToFilter
        );
    }
}
